import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useYodleeFastLink from 'Hooks/Aggregators/useYodleeFastLink';
import Sentry from 'Integrations/Sentry';
import { useSnackbarFunctionsContext } from 'Context/SnackbarContext';
import useBodyScrollLock from 'Hooks/useBodyScrollLock';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import DataContainer from 'Components/UI/DataContainer';
import { importYodleeFastLink } from 'Helpers/External';
import { YODLEE_FASTLINK_ELEMENT_ID } from '.';

const YodleeLinkContext = React.createContext(undefined);

export const SYNC_STATUS_MAP = {
  waiting: 'waiting',
  working: 'working',
  finished: 'finished',
  failed: 'failed',
};

// Actions
const SHOW_CONNECT_VIEW = 'show-connect-view';
const SHOW_CONNECT_VIEW_FAILED = 'show-connect-view-failed';
const HIDE_CONNECT_VIEW = 'hide-connect-view';

const SYNC_STARTED = 'sync-started';
const SYNC_FINISHED = 'sync-finished';

const getInitialState = () => {
  return {
    showConnectView: false,
    showConnectViewError: null,
    syncStatus: SYNC_STATUS_MAP.waiting,
    syncError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SHOW_CONNECT_VIEW: {
      return {
        ...state,
        showConnectView: true,
      };
    }
    case SHOW_CONNECT_VIEW_FAILED: {
      return {
        ...state,
        showConnectViewError: action.showConnectViewError,
      };
    }
    case HIDE_CONNECT_VIEW: {
      return {
        ...state,
        showConnectView: false,
      };
    }
    case SYNC_STARTED: {
      return {
        ...state,
        syncStatus: SYNC_STATUS_MAP.working,
      };
    }
    case SYNC_FINISHED: {
      return {
        ...state,
        syncStatus: SYNC_STATUS_MAP.finished,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

// DO NOT EXPORT: this provider is not safe for consumption because Yodlee's FastLink JS is not guaranteed to be loaded by the time this provider is rendered!
// Use YodleeLinkProvider instead, which wraps this in a piece of code that waits on Yodlee's FastLink JS to be loaded
const ActualYodleeLinkProvider = ({
  resource,
  yodleeConfigLink,
  children,
  onLoadLink,
  handleRefreshSuccess,
  onComplete,
  onSyncStarted,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { syncStatus } = state;

  const { displayErrorMessage, displaySuccess, displayWarning } = useSnackbarFunctionsContext();

  const handleFailure = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.failure, {
      eventProperties: {
        aggregator: 'yodlee',
        institution: resource.attr('institutionName'),
      },
    });
    if (onLoadLink) {
      await onLoadLink(resource.relationship(fr.relationships.onFailure.name).link('related'));
    }

    if (onComplete) {
      await onComplete();
    }
  }, []);

  const handleClose = React.useCallback(async (yodleeResponseData, yodleeConfigResource) => {
    const sites = yodleeResponseData.sites || [];

    const hadFailure = sites.reduce((acc, curr) => {
      return acc || curr.status === 'FAILED';
    }, false);

    if (hadFailure) {
      await handleFailure();
      return;
    }

    dispatch({ type: HIDE_CONNECT_VIEW });

    const providerAccountId = sites.reduce((acc, curr) => {
      if (curr.status === 'SUCCESS') {
        return curr.providerAccountId;
      }

      return acc;
    }, null);

    if (providerAccountId === null) {
      if (onComplete) {
        await onComplete();
      }
      return;
    }

    try {
      dispatch({ type: SYNC_STARTED });
      if (onSyncStarted) {
        await onSyncStarted();
      }

      if (handleRefreshSuccess) {
        await handleRefreshSuccess();
        return;
      }

      // this is a new link
      yodleeConfigResource.attr('providerAccountId', providerAccountId.toString());
      await yodleeConfigResource.submit();
      dispatch({ type: SYNC_FINISHED });
      if (onComplete) {
        await onComplete();
      }
      logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.success, {
        eventProperties: {
          aggregator: 'yodlee',
          institution: resource.attr('institutionName'),
        },
      });
      displaySuccess('Successfully synced accounts');
    } catch (err) {
      Sentry.createSentryErrorLog(err);
      displayErrorMessage(err, 'Failed to sync accounts');
      await handleFailure();
    }
  }, []);

  const handleErrorLoadingYodlee = React.useCallback(async (error) => {
    displayErrorMessage(error, 'Failed to load automated linking service. Please refresh and try again or setup manually');
  });

  const handleOnTimeout = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.timeout, {
      eventProperties: {
        aggregator: 'yodlee',
        institution: resource.attr('institutionName'),
      },
    });
    dispatch({ type: HIDE_CONNECT_VIEW });
    displayWarning('Unfortunately, the attempt to link your bank timed out. You can either try again or add manually.');
  }, []);

  const handleSyncStatusChange = async (syncStatus2) => {
    switch (syncStatus2) {
      case SYNC_STATUS_MAP.finished:
        if (onLoadLink) {
          await onLoadLink(resource.relationship(fr.relationships.onSuccess.name).link('related'));
        }
        break;
      /* no default */
    }
  };

  React.useEffect(() => {
    handleSyncStatusChange(syncStatus);
  }, [syncStatus]);

  useBodyScrollLock(state.showConnectView);

  const {
    handleClickInitiate,
    isInitialized,
    initializationError,
  } = useYodleeFastLink(resource, YODLEE_FASTLINK_ELEMENT_ID, {
    yodleeConfigLink,
    onClose: handleClose,
    onTimeout: handleOnTimeout,
    onErrorLoadingYodlee: handleErrorLoadingYodlee,
  });

  const handleClick = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.initializing, {
      eventProperties: {
        aggregator: 'yodlee',
        institution: resource.attr('institutionName'),
      },
    });
    try {
      dispatch({ type: SHOW_CONNECT_VIEW });
      await handleClickInitiate();
    } catch (err) {
      dispatch({ type: SHOW_CONNECT_VIEW_FAILED, showConnectViewError: err });
    }
  }, [handleClickInitiate]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitialized,
      initializationError,
      showConnectView: state.showConnectView,
      showConnectViewError: state.showConnectViewError,
      syncStatus: state.syncStatus,
      syncError: state.syncError,
      handleClick,
    };
  }, [
    state,
    isInitialized,
    initializationError,
    handleClick,
  ]);

  return (
    <YodleeLinkContext.Provider value={memoizedValue}>
      {children}
    </YodleeLinkContext.Provider>
  );
};

ActualYodleeLinkProvider.defaultProps = {
  handleRefreshSuccess: undefined,
  yodleeConfigLink: undefined,
  onComplete: undefined,
  onLoadLink: undefined,
  onSyncStarted: undefined,
};

ActualYodleeLinkProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  resource: PropTypes.object.isRequired,
  yodleeConfigLink: PropTypes.object,
  onLoadLink: PropTypes.func,
  handleRefreshSuccess: PropTypes.func,
  onComplete: PropTypes.func,
  onSyncStarted: PropTypes.func,
};

export const YodleeLinkProvider = (props) => {
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState();
  useEffect(() => {
    importYodleeFastLink()
      .then(() => setLoaded(true))
      .catch(setErr);
  }, []);

  return (
    <DataContainer
      loading={!loaded}
      loadingError={err}
      component={ActualYodleeLinkProvider}
      componentProps={props}
    />
  );
};

// Use Survey State Context
export const useYodleeLinkContext = () => {
  const context = React.useContext(YodleeLinkContext);
  if (context === undefined) {
    throw new Error('useYodleeLinkContext must be used within a YodleeLinkProvider');
  }
  return context;
};
